import React from 'react';
import PropTypes from 'prop-types';

const ActivityDetails = ({
    activityData,
    selectedDate,
    selectedUser,
    onClose,
    loading,
    calendarDays
}) => {
    // Format the date for display
    const formattedDate = selectedDate ? new Date(selectedDate).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }) : '';

    const userLabel = selectedUser?.username || selectedUser?.email || selectedUser?.name || 'User';

    if (loading) {
        return (
            <div className="activity-details">
                <div className="activity-details-header">
                    <h2>Loading Details...</h2>
                    <button className="close-button" onClick={onClose}>&times;</button>
                </div>
                <div className="activity-details-body">
                    <div className="loading-spinner">Loading activity details...</div>
                </div>
            </div>
        );
    }

    if (!activityData || (Array.isArray(activityData) && activityData.length === 0) || (Array.isArray(activityData) && activityData.every(a => !a.itemType && !a.itemId && !a.description && !a.timestamp))) {
        // Try to show a fallback using calendarDays if available
        let calendarInfo = null;
        if (selectedDate && Array.isArray(calendarDays)) {
            const day = calendarDays.find(d => d.date === selectedDate);
            if (day && day.activities && Object.keys(day.activities).length > 0) {
                calendarInfo = Object.entries(day.activities)
                    .map(([type, count]) => `${count} ${type}`)
                    .join(', ');
            }
        }

        return (
            <div className="activity-details">
                <div className="activity-details-header">
                    <h2>{userLabel} - {formattedDate}</h2>
                    <button className="close-button" onClick={onClose}>&times;</button>
                </div>
                <div className="activity-details-body">
                    <div className="no-data">
                        {calendarInfo
                            ? `${userLabel} completed ${calendarInfo} on this day.`
                            : `No tasks completed on this day.`}
                    </div>
                </div>
            </div>
        );
    }

    // Calculate summary
    const totalTasks = activityData.length;
    // Count by type
    const typeCounts = activityData.reduce((acc, curr) => {
        acc[curr.itemType] = (acc[curr.itemType] || 0) + 1;
        return acc;
    }, {});
    const typeSummary = Object.entries(typeCounts)
        .map(([type, count]) => `${count} ${type}`)
        .join(', ');

    return (
        <div className="activity-details">
            <div className="activity-details-header">
                <h2>{userLabel} - {formattedDate}</h2>
                <button className="close-button" onClick={onClose}>&times;</button>
            </div>
            <div className="activity-details-body">
                <div className="details-summary">
                    <span className="summary-count">{totalTasks}</span>
                    <span className="summary-text">
                        task{totalTasks !== 1 ? 's' : ''} completed
                        {typeSummary ? ` (${typeSummary})` : ''}
                    </span>
                </div>
                <table className="activity-table">
                    <thead>
                        <tr>
                            <th>Time</th>
                            <th>Type</th>
                            <th>Item ID</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        {activityData.map((activity, index) => (
                            <tr key={index}>
                                <td>
                                    {new Date(activity.timestamp).toLocaleTimeString('en-US', {
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </td>
                                <td>{activity.itemType}</td>
                                <td>{activity.itemId}</td>
                                <td>{activity.description || '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

ActivityDetails.propTypes = {
    activityData: PropTypes.array,
    selectedDate: PropTypes.string,
    selectedUser: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    calendarDays: PropTypes.array
};

ActivityDetails.defaultProps = {
    loading: false
};

export default ActivityDetails;
